import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowsRightLeftIcon, EyeIcon, EyeSlashIcon, ShieldCheckIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import Dropdown from "../Settings/Dropdown";
import { api, FeatureRequestDetail, FeatureRequestItem, FeatureRequestStatus, STATUS_META, STATUS_ORDER } from "./api";
import ConfirmModal from "./ConfirmModal";

interface Props {
  request: FeatureRequestDetail;
  onChanged: (updated: FeatureRequestItem) => void;
}

const field = "w-full rounded-lg border border-gray-300 dark:border-base-300 bg-base-100 px-3 py-2 text-sm text-base-content placeholder:text-base-content/40 outline-none focus:border-blue-500 transition-colors";
const label = "block text-[11px] font-semibold uppercase tracking-wider text-base-content/60 mb-1.5";

// Rendered only for SUPER_ADMIN_EMAILS accounts (the server enforces it).
// Status + notes save together; a status change asks before emailing.
const StaffControls: React.FC<Props> = ({ request, onChanged }) => {
  const navigate = useNavigate();
  const [status, setStatus] = useState<FeatureRequestStatus>(request.status);
  const [releaseNote, setReleaseNote] = useState(request.release_note || "");
  const [staffResponse, setStaffResponse] = useState(request.staff_response || "");
  const [saving, setSaving] = useState(false);
  const [notifyPrompt, setNotifyPrompt] = useState<number | null>(null);
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeQuery, setMergeQuery] = useState("");
  const [mergeResults, setMergeResults] = useState<FeatureRequestItem[]>([]);
  const [mergeTarget, setMergeTarget] = useState<FeatureRequestItem | null>(null);
  const [hidePrompt, setHidePrompt] = useState(false);

  useEffect(() => {
    setStatus(request.status);
    setReleaseNote(request.release_note || "");
    setStaffResponse(request.staff_response || "");
  }, [request.id, request.status, request.release_note, request.staff_response]);

  useEffect(() => {
    if (!mergeOpen) return;
    const q = mergeQuery.trim();
    if (q.length < 2) {
      setMergeResults([]);
      return;
    }
    const t = setTimeout(() => {
      api
        .list({ q, sort: "top" })
        .then((r) => setMergeResults(r.items.filter((i) => i.id !== request.id).slice(0, 6)))
        .catch(() => setMergeResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [mergeQuery, mergeOpen, request.id]);

  const dirty = status !== request.status || releaseNote !== (request.release_note || "") || staffResponse !== (request.staff_response || "");
  const statusChanged = status !== request.status;
  const willNotify = statusChanged && ["planned", "in_progress", "delivered"].includes(status);

  const save = async (notify: boolean) => {
    setSaving(true);
    try {
      const updated = await api.setStatus(request.id, { status, release_note: releaseNote, staff_response: staffResponse, notify });
      onChanged(updated);
      toast.success(statusChanged ? `Marked ${STATUS_META[status].label}${notify ? ", people notified" : ""}` : "Saved");
      setNotifyPrompt(null);
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const onSaveClick = async () => {
    if (!willNotify) return save(false);
    try {
      const count = await api.notifyCount(request.id);
      if (count === 0) return save(false);
      setNotifyPrompt(count);
    } catch {
      setNotifyPrompt(0);
    }
  };

  const merge = async () => {
    if (!mergeTarget) return;
    setSaving(true);
    try {
      await api.merge(request.id, mergeTarget.id);
      toast.success(`Merged into "${mergeTarget.title}"`);
      navigate(`/feature-requests/${mergeTarget.id}`, { replace: true });
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Could not merge");
    } finally {
      setSaving(false);
      setMergeTarget(null);
      setMergeOpen(false);
    }
  };

  const toggleHidden = async () => {
    setSaving(true);
    try {
      const updated = request.hidden_at ? await api.unhide(request.id) : await api.hide(request.id);
      onChanged(updated);
      toast.success(updated.hidden_at ? "Hidden from officers" : "Visible again");
    } catch (e: any) {
      toast.error(e?.response?.data?.error || "Could not update visibility");
    } finally {
      setSaving(false);
      setHidePrompt(false);
    }
  };

  return (
    <section className="rounded-xl border border-amber-200 dark:border-amber-900 bg-amber-50/40 dark:bg-amber-950/20 p-4 space-y-4">
      <header className="flex items-center gap-2">
        <ShieldCheckIcon className="size-5 text-amber-600 dark:text-amber-400" />
        <h2 className="font-semibold text-sm">Staff</h2>
        <span className="text-xs text-base-content/50">Only you can see this panel.</span>
        <div className="ml-auto flex items-center gap-2">
          <Button variant="outline" size="xs" onClick={() => setMergeOpen((v) => !v)} disabled={saving || Boolean(request.merged_into_id)}>
            <ArrowsRightLeftIcon className="size-3.5" /> Merge into…
          </Button>
          <Button variant={request.hidden_at ? "outline" : "danger"} size="xs" onClick={() => setHidePrompt(true)} disabled={saving}>
            {request.hidden_at ? <EyeIcon className="size-3.5" /> : <EyeSlashIcon className="size-3.5" />} {request.hidden_at ? "Unhide" : "Hide"}
          </Button>
        </div>
      </header>

      {mergeOpen && (
        <div className="rounded-lg border border-base-300 bg-base-100 p-3 space-y-2">
          <label className={label}>Fold this request into</label>
          <input autoFocus className={field} value={mergeQuery} onChange={(e) => setMergeQuery(e.target.value)} placeholder="Search by title…" />
          {mergeResults.length > 0 && (
            <ul className="divide-y divide-base-300">
              {mergeResults.map((r) => (
                <li key={r.id}>
                  <button type="button" className="w-full flex items-center gap-2 py-2 text-left text-sm hover:text-blue-600 dark:hover:text-blue-300" onClick={() => setMergeTarget(r)}>
                    <span className="tabular-nums text-xs text-base-content/50 w-8">▲ {r.vote_count}</span>
                    <span className="truncate flex-1">{r.title}</span>
                    <span className={cn("text-[10px] px-1.5 py-0.5 rounded-full", STATUS_META[r.status].chip)}>{STATUS_META[r.status].label}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="text-[11px] text-base-content/50">Votes and comments move to the target; this request is hidden and old links redirect.</p>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-[12rem_1fr] gap-4">
        <div>
          <label className={label}>Status</label>
          <Dropdown size="sm" menuAlign="start" options={STATUS_ORDER.map((s) => ({ value: s, label: STATUS_META[s].label }))} value={status} onChange={(v) => setStatus(v as FeatureRequestStatus)} />
          <p className="mt-1.5 text-[11px] text-base-content/50">{STATUS_META[status].hint}</p>
        </div>
        <div className="space-y-3">
          <div>
            <label className={label} htmlFor="fr-staff-response">Official response <span className="normal-case tracking-normal font-normal text-base-content/40">· shown above the comments</span></label>
            <textarea id="fr-staff-response" rows={2} className={cn(field, "resize-none leading-5")} value={staffResponse} onChange={(e) => setStaffResponse(e.target.value)} placeholder="What we're thinking, what we need to know, or why not." maxLength={4000} />
          </div>
          {(status === "delivered" || releaseNote) && (
            <div>
              <label className={label} htmlFor="fr-release-note">Release note <span className="normal-case tracking-normal font-normal text-base-content/40">· shown in the changelog</span></label>
              <textarea id="fr-release-note" rows={2} className={cn(field, "resize-none leading-5")} value={releaseNote} onChange={(e) => setReleaseNote(e.target.value)} placeholder="What shipped and where to find it." maxLength={4000} />
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end gap-2">
        {willNotify && <span className="text-[11px] text-base-content/50">Changing to {STATUS_META[status].label} emails the author and upvoters.</span>}
        <Button variant="primary" size="sm" onClick={onSaveClick} disabled={!dirty || saving}>
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>

      <ConfirmModal
        open={notifyPrompt !== null}
        title={`Mark as ${STATUS_META[status].label}?`}
        body={notifyPrompt ? `${notifyPrompt} ${notifyPrompt === 1 ? "person" : "people"} who posted or upvoted this request will get an email.` : "The author and upvoters will get an email."}
        confirmLabel="Save and notify"
        secondaryLabel="Save without notifying"
        onSecondary={() => save(false)}
        busy={saving}
        onConfirm={() => save(true)}
        onClose={() => setNotifyPrompt(null)}
      />
      <ConfirmModal
        open={Boolean(mergeTarget)}
        title="Merge this request?"
        body={mergeTarget ? <>Fold <b>{request.title}</b> into <b>{mergeTarget.title}</b>. Votes and comments move over; this can't be undone.</> : null}
        confirmLabel="Merge"
        variant="danger"
        busy={saving}
        onConfirm={merge}
        onClose={() => setMergeTarget(null)}
      />
      <ConfirmModal
        open={hidePrompt}
        title={request.hidden_at ? "Show this request again?" : "Hide this request?"}
        body={request.hidden_at ? "Officers will see it on the board again." : "Officers won't see it anywhere. Votes and comments are kept, and staff can unhide it later."}
        confirmLabel={request.hidden_at ? "Unhide" : "Hide"}
        variant={request.hidden_at ? "primary" : "danger"}
        busy={saving}
        onConfirm={toggleHidden}
        onClose={() => setHidePrompt(false)}
      />
    </section>
  );
};

export default StaffControls;
